import type { ToolCallState } from '@/hooks/use-streaming-v2';
import { PhaseShell, InfoRowsSkeleton } from './ToolResultRendererSkeleton';

interface ModelInfoData {
  data: {
    name?: string;
    version?: string;
    inputs?: Array<{ name: string; displayName: string }>;
    outputs?: Array<{ name: string; displayName: string }>;
    [key: string]: unknown;
  };
}

function isModelInfoResult(data: unknown): data is ModelInfoData {
  return (
    typeof data === 'object' &&
    data !== null &&
    'data' in data &&
    typeof (data as ModelInfoData).data === 'object'
  );
}

function renderComplete(result: ModelInfoData) {
  const { data } = result;
  const inputs = data.inputs ?? [];
  const outputs = data.outputs ?? [];
  return (
    <div className="space-y-2.5">
      <div className="text-xs font-mono font-semibold text-accent tracking-[1.2px] uppercase">
        Model Info
      </div>
      <div className="flex items-center gap-3">
        {data.name && (
          <span className="text-sm font-mono text-text-bright">{data.name}</span>
        )}
        {data.version && (
          <span className="text-xs font-mono text-text-muted px-1.5 py-0.5 bg-panel-border/50 rounded-[2px]">
            {data.version}
          </span>
        )}
      </div>
      <div className="flex gap-4 text-sm font-mono">
        <span className="text-text-muted">
          Inputs: <span className="text-text-bright tabular-nums">{inputs.length}</span>
        </span>
        <span className="text-text-muted">
          Outputs: <span className="text-text-bright tabular-nums">{outputs.length}</span>
        </span>
      </div>
      {inputs.length > 0 && (
        <div>
          <div className="text-xs font-mono text-text-dim uppercase tracking-[1px] mb-1">Inputs</div>
          <div className="grid grid-cols-2 gap-x-3 gap-y-0.5">
            {inputs.map((inp) => (
              <div key={inp.name} className="text-xs font-mono text-text-muted truncate">
                {inp.displayName}
              </div>
            ))}
          </div>
        </div>
      )}
      {outputs.length > 0 && (
        <div>
          <div className="text-xs font-mono text-text-dim uppercase tracking-[1px] mb-1">Outputs</div>
          <div className="grid grid-cols-2 gap-x-3 gap-y-0.5">
            {outputs.map((out) => (
              <div key={out.name} className="text-xs font-mono text-text-muted truncate">
                {out.displayName}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

interface ModelInfoPanelProps {
  state: ToolCallState;
}

/**
 * Phase-aware ModelInfoPanel. Per directive §7.6 — this tool runs <100ms so
 * the skeleton phase is rarely visible; trivial info-rows skeleton suffices.
 */
export function ModelInfoPanel({ state }: ModelInfoPanelProps) {
  if (state.phase === 'complete' && isModelInfoResult(state.result)) {
    return renderComplete(state.result);
  }
  if (state.phase === 'complete') {
    return (
      <div className="text-xs font-mono text-text-muted">
        <pre className="whitespace-pre-wrap">{JSON.stringify(state.result, null, 2)}</pre>
      </div>
    );
  }
  const variant = state.phase === 'aborted' ? 'aborted'
    : state.phase === 'executing' ? 'in-progress'
    : 'skeleton';
  return (
    <PhaseShell variant={variant} title="Model Info">
      <InfoRowsSkeleton rows={3} />
    </PhaseShell>
  );
}
